/**
 * PlanGate — conditionally renders children based on plan feature availability.
 *
 * Usage:
 *   <PlanGate feature="messaging" fallback={<UpgradePrompt feature="messaging" />}>
 *     <MessagingPanel />
 *   </PlanGate>
 */

import type { ReactNode } from 'react';
import { usePlanGate } from '../../hooks/usePlanGate';

type PlanGateProps = {
  feature: string;
  children: ReactNode;
  fallback?: ReactNode;
};

export const PlanGate = ({ feature, children, fallback }: PlanGateProps) => {
  const { hasFeature } = usePlanGate();

  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  return <>{fallback ?? <UpgradePrompt feature={feature} />}</>;
};

export const UpgradePrompt = ({ feature }: { feature: string }) => {
  const { plan } = usePlanGate();

  const featureLabels: Record<string, string> = {
    messaging: 'Team messaging',
    dm: 'Direct messages',
    api_keys: 'API keys',
    keycloak: 'Keycloak SSO',
    custom_policies: 'Custom policies',
    org_admin_controls: 'Organization-wide controls',
    invite_members: 'Invite team members',
  };

  const label = featureLabels[feature] ?? feature;

  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-6 text-center">
      <div className="text-sm font-medium text-neutral-700 mb-1">
        {label} is not available on your current plan
      </div>
      <div className="text-xs text-neutral-400 mb-3">
        You are on the <span className="font-medium capitalize">{plan}</span> plan.
      </div>
      <button
        type="button"
        className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
        onClick={() => {
          // TODO: navigate to billing/upgrade page when implemented
          window.alert('Upgrade flow coming soon.');
        }}
      >
        Upgrade Plan
      </button>
    </div>
  );
};

export const LimitBadge = ({
  current,
  limit,
}: {
  current: number;
  limit: number | null;
}) => {
  if (limit === null) {
    return <span className="text-xs text-neutral-400">{current} (unlimited)</span>;
  }

  const pct = (current / limit) * 100;
  const color =
    pct >= 100
      ? 'text-red-600'
      : pct >= 80
        ? 'text-amber-600'
        : 'text-neutral-500';

  return (
    <span className={`text-xs font-medium ${color}`}>
      {current} / {limit}
    </span>
  );
};
